import { parseIsoMs } from "@/features/opportunities/lib/format-relative-time";
import type { EventJobTimeseries } from "@/features/events/api/events.api";

const MS_PER_MINUTE = 60_000;
const PERCENT = 100;
const MINUTES_PER_DAY = 24 * 60;
const HOURS_PER_DAY = 24;

export interface EventActivityPoint {
  /** ISO start of the bucket, and the `publishedFrom` bound for drilling in. */
  bucketStart: string;
  /** Inclusive `publishedTo` bound — the API filters with lte, not lt. */
  bucketEnd: string;
  /** Axis tick: as short as the window allows without becoming ambiguous. */
  label: string;
  /** Tooltip heading: the span in full, because the axis abbreviates it. */
  rangeLabel: string;
  total: number;
  completed: number;
  failed: number;
  inFlight: number;
  /** Percentage 0–100, or null for an empty bucket where a rate means nothing. */
  failureRate: number | null;
  avgRunMs: number | null;
  p95RunMs: number | null;
  /** p95 of the whole wait — click to completion, queue time included. */
  p95EndToEndMs: number | null;
  /** p95 of publish → pickup: the part of the wait nobody was working. */
  p95QueueWaitMs: number | null;
}

export interface EventActivitySeries {
  points: EventActivityPoint[];
  total: number;
  failed: number;
  /**
   * Bucket carrying the most failures — the answer to "when did it break".
   * Null when the window is clean.
   */
  worstBucket: EventActivityPoint | null;
  /**
   * Whether each latency series has anything to plot. Tracked separately
   * because they come from different columns and go missing independently: a
   * window of events that were picked up but never finished has queue waits and
   * no end-to-end at all. A series with nothing in it is suppressed along with
   * its axis rather than drawn flat against zero.
   */
  hasRunLatency: boolean;
  hasEndToEndLatency: boolean;
  /**
   * Tallest latency reading in the window, for deciding whether a threshold
   * line is worth drawing. A budget far above the data would stretch the axis
   * and press the whole series flat against the floor — losing the shape the
   * chart exists for, to show a line nothing is near.
   */
  maxLatencyMs: number | null;
}

const EMPTY_SERIES: EventActivitySeries = {
  points: [],
  total: 0,
  failed: 0,
  worstBucket: null,
  hasRunLatency: false,
  hasEndToEndLatency: false,
  maxLatencyMs: null,
};

/**
 * Turns the API's buckets into rows the chart can render directly. Kept apart
 * from the component so the arithmetic that decides what an operator reads —
 * failure rate, the worst bucket, whether latency is plottable — is testable
 * without mounting a chart.
 */
export function buildEventActivitySeries(
  data: EventJobTimeseries | undefined,
  locale?: string,
): EventActivitySeries {
  if (!data || data.buckets.length === 0) return EMPTY_SERIES;

  const spanMs = data.bucketMinutes * MS_PER_MINUTE;
  // A clock time alone only identifies a bucket while the window fits in a day.
  // Past that the axis has to carry the date, and once a bucket *is* a day the
  // clock time says nothing at all.
  const isWholeDayBucket = data.bucketMinutes >= MINUTES_PER_DAY;
  const needsDate = isWholeDayBucket || data.windowHours > HOURS_PER_DAY;

  const points = data.buckets.map((bucket): EventActivityPoint => {
    const startMs = parseIsoMs(bucket.bucketStart);
    const endMs = startMs == null ? null : startMs + spanMs;

    return {
      bucketStart: bucket.bucketStart,
      // One millisecond short of the next bucket's start, so an event on the
      // boundary belongs to exactly one bar.
      bucketEnd: endMs == null ? bucket.bucketStart : new Date(endMs - 1).toISOString(),
      label: isWholeDayBucket
        ? formatDate(startMs, locale)
        : needsDate
          ? `${formatDate(startMs, locale)} ${formatClockTime(startMs, locale)}`
          : formatClockTime(startMs, locale),
      rangeLabel: isWholeDayBucket
        ? formatDate(startMs, locale)
        : formatRange(startMs, endMs, locale),
      total: bucket.total,
      completed: bucket.completed,
      failed: bucket.failed,
      inFlight: bucket.inFlight,
      failureRate: bucket.total > 0 ? (bucket.failed / bucket.total) * PERCENT : null,
      avgRunMs: bucket.avgRunMs,
      p95RunMs: bucket.p95RunMs,
      p95EndToEndMs: bucket.p95EndToEndMs,
      p95QueueWaitMs: bucket.p95QueueWaitMs,
    };
  });

  return {
    points,
    total: points.reduce((sum, point) => sum + point.total, 0),
    failed: points.reduce((sum, point) => sum + point.failed, 0),
    worstBucket: findWorstBucket(points),
    hasRunLatency: points.some((point) => point.p95RunMs != null),
    hasEndToEndLatency: points.some((point) => point.p95EndToEndMs != null),
    maxLatencyMs: findMaxLatency(points),
  };
}

/**
 * The tallest reading across both latency series, since both share one axis.
 * Null when neither has a value — there is no scale to compare a budget to.
 */
function findMaxLatency(points: EventActivityPoint[]): number | null {
  let max: number | null = null;
  for (const point of points) {
    for (const value of [point.p95EndToEndMs, point.p95RunMs]) {
      if (value != null && (max == null || value > max)) max = value;
    }
  }
  return max;
}

/**
 * How far above the tallest reading a budget may sit and still be drawn. Past
 * this the line stretches the axis and flattens the series against the floor,
 * costing the shape of the window to display a threshold nothing is near. A
 * budget out of frame is not a breach worth showing — the series being far
 * below it is itself the answer.
 */
const BUDGET_LINE_MAX_OVERSHOOT = 1.5;

/**
 * Whether the slow-action budget is worth drawing across this window. Kept here
 * rather than in the chart so the rule that hides it is testable without
 * mounting recharts.
 */
export function shouldShowBudgetLine(
  budgetMs: number | null,
  maxLatencyMs: number | null,
): boolean {
  if (budgetMs == null || budgetMs <= 0 || maxLatencyMs == null) return false;
  return budgetMs <= maxLatencyMs * BUDGET_LINE_MAX_OVERSHOOT;
}

/**
 * Labels a range without going through the series, so a pasted URL can name its
 * own selection before — or without — the chart data arriving.
 */
export function formatEventActivityRangeLabel(
  from: string,
  to: string,
  locale?: string,
): string {
  const startMs = parseIsoMs(from);
  const inclusiveEndMs = parseIsoMs(to);
  return formatRange(startMs, inclusiveEndMs == null ? null : inclusiveEndMs + 1, locale);
}

/**
 * Ties go to the later bucket: when two hours failed equally, the recent one is
 * the one still worth investigating.
 */
function findWorstBucket(points: EventActivityPoint[]): EventActivityPoint | null {
  let worst: EventActivityPoint | null = null;
  for (const point of points) {
    if (point.failed > 0 && (worst == null || point.failed >= worst.failed)) {
      worst = point;
    }
  }
  return worst;
}

function formatClockTime(ms: number | null, locale?: string): string {
  if (ms == null) return "—";
  return new Date(ms).toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatDate(ms: number | null, locale?: string): string {
  if (ms == null) return "—";
  return new Date(ms).toLocaleDateString(locale, { day: "numeric", month: "short" });
}

function formatRange(startMs: number | null, endMs: number | null, locale?: string): string {
  if (startMs == null || endMs == null) return "—";
  return `${formatDate(startMs, locale)}, ${formatClockTime(startMs, locale)} – ${formatClockTime(endMs, locale)}`;
}
